import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../styles/design-system';
import { useNavigate } from 'react-router-dom';
import {
    Mail,
    Lock,
    User,
    MapPin,
    Calendar,
    Clock,
    ArrowRight,
    ArrowLeft,
    Check,
    Loader2,
    Globe
} from 'lucide-react';

const AuthPage = ({ initialMode = 'login' }) => {
    const { login, signup, loading, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [authMode, setAuthMode] = useState(initialMode);
    const [otpSent, setOtpSent] = useState(false);
    const [step, setStep] = useState(1); // 1: Account, 2: Astrology

    // Form State
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        username: '',
        full_name: '',
        phone: '',
        otp: '',
        birth_data: {
            name: '',
            day: new Date().getDate(),
            month: new Date().getMonth() + 1,
            year: 1990,
            hour: 12,
            minute: 0,
            latitude: '',
            longitude: '',
            timezone: 5.5,
            place_of_birth: ''
        }
    });

    // Location Search State
    const [locationQuery, setLocationQuery] = useState('');
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchTimeoutRef = useRef(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        setAuthMode(initialMode);
    }, [initialMode]);

    // Auto-advance to profile completion if logged in
    useEffect(() => {
        if (isAuthenticated && initialMode === 'signup') {
            setStep(2);
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    username: user.username || '',
                    full_name: user.full_name || '',
                    email: user.email || '',
                    phone: user.username && user.username.startsWith('+') ? user.username : ''
                }));
            }
        }
    }, [isAuthenticated, initialMode, user]);

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
        setError('');
    };

    const handleLocationSearch = async (query) => {
        setLocationQuery(query);
        if (query.length < 3) {
            setLocationSuggestions([]);
            return;
        }

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        setIsSearchingLocation(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                const response = await fetch(
                    `${API_URL}/api/proxy/location-search?q=${encodeURIComponent(query)}`
                );
                const data = await response.json();
                setLocationSuggestions(data);
                setShowSuggestions(true);
            } catch (err) {
                console.error('Location search error:', err);
            } finally {
                setIsSearchingLocation(false);
            }
        }, 500);
    };

    const selectLocation = (location) => {
        handleInputChange('birth_data.place_of_birth', location.display_name.split(',')[0]);
        handleInputChange('birth_data.latitude', parseFloat(location.lat));
        handleInputChange('birth_data.longitude', parseFloat(location.lon));
        setLocationQuery(location.display_name);
        setShowSuggestions(false);

        const offset = Math.round((parseFloat(location.lon) / 15) * 2) / 2;
        handleInputChange('birth_data.timezone', offset);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (authMode === 'otp') {
            // OTP Login Flow
            if (!otpSent) {
                try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                    const response = await fetch(`${API_URL}/api/otp-auth/send-otp`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ phone: formData.phone })
                    });
                    const data = await response.json();

                    if (data.success) {
                        setOtpSent(true);
                        setSuccess('OTP sent successfully! Check your phone.');
                    } else {
                        setError(data.message || 'Failed to send OTP');
                    }
                } catch (error) {
                    setError('Failed to send OTP. Please try again.');
                }
            } else {
                try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                    const response = await fetch(`${API_URL}/api/otp-auth/verify-otp`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ phone: formData.phone, otp: formData.otp })
                    });
                    const data = await response.json();

                    if (data.success) {
                        localStorage.setItem('access_token', data.data.tokens.access_token);
                        localStorage.setItem('refresh_token', data.data.tokens.refresh_token);
                        localStorage.setItem('user', JSON.stringify(data.data.user));

                        if (data.data.profile_complete) {
                            setSuccess('Login successful!');
                            setTimeout(() => {
                                window.location.href = '/home';
                            }, 1000);
                        } else {
                            setSuccess('Please complete your profile details');
                            setAuthMode('signup');
                            setStep(2);
                            setFormData(prev => ({
                                ...prev,
                                username: data.data.user.username || formData.phone,
                                phone: formData.phone
                            }));
                        }
                    } else {
                        setError(data.message || 'Invalid OTP');
                    }
                } catch (error) {
                    setError('Failed to verify OTP. Please try again.');
                }
            }
            return;
        }

        // Email Login/Signup
        if (authMode === 'signup') {
            if (step === 1) {
                if (formData.password !== formData.confirmPassword) {
                    setError('Passwords do not match');
                    return;
                }
                setStep(2);
                return;
            }
        }

        try {
            if (authMode === 'login') {
                await login(formData.email, formData.password);
                setSuccess('Login successful!');
                navigate('/home');
            } else {
                const signupData = {
                    email: formData.email,
                    username: formData.username,
                    password: formData.password,
                    full_name: formData.full_name,
                    birth_data: formData.birth_data
                };

                const token = localStorage.getItem('access_token');
                if (token) {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                    const response = await fetch(`${API_URL}/auth/complete-profile`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(signupData)
                    });
                    if (!response.ok) throw new Error('Failed to complete profile');
                    setSuccess('Profile completed successfully!');
                    window.location.href = '/home';
                } else {
                    await signup(signupData);
                    setSuccess('Account created successfully! Please login.');
                    setAuthMode('login');
                    setStep(1);
                }
            }
        } catch (err) {
            setError(err.message || 'Authentication failed');
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                {/* Header */}
                <div style={styles.header}>
                    <h2 style={styles.title}>
                        {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
                    </h2>
                    <p style={styles.subtitle}>
                        {authMode === 'login'
                            ? 'Sign in to access your AI trading dashboard'
                            : 'Join the future of numerology-based trading'}
                    </p>
                </div>

                {/* Content */}
                <div style={styles.content}>
                    {/* Tabs */}
                    <div style={styles.tabs}>
                        <button
                            style={{ ...styles.tab, ...(authMode === 'login' ? styles.activeTab : {}) }}
                            onClick={() => { setAuthMode('login'); setOtpSent(false); navigate('/login'); }}
                        >
                            Email Login
                        </button>
                        <button
                            style={{ ...styles.tab, ...(authMode === 'otp' ? styles.activeTab : {}) }}
                            onClick={() => { setAuthMode('otp'); setOtpSent(false); }}
                        >
                            OTP Login
                        </button>
                        <button
                            style={{ ...styles.tab, ...(authMode === 'signup' ? styles.activeTab : {}) }}
                            onClick={() => { setAuthMode('signup'); setOtpSent(false); navigate('/signup'); }}
                        >
                            Sign Up
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={styles.form}>
                        {authMode === 'login' ? (
                            <>
                                <div style={styles.inputGroup}>
                                    <Mail size={18} style={styles.inputIcon} />
                                    <input
                                        type="email"
                                        placeholder="Email Address"
                                        value={formData.email}
                                        onChange={e => handleInputChange('email', e.target.value)}
                                        style={styles.input}
                                        required
                                        autoComplete="username"
                                    />
                                </div>
                                <div style={styles.inputGroup}>
                                    <Lock size={18} style={styles.inputIcon} />
                                    <input
                                        type="password"
                                        placeholder="Password"
                                        value={formData.password}
                                        onChange={e => handleInputChange('password', e.target.value)}
                                        style={styles.input}
                                        required
                                        autoComplete="current-password"
                                    />
                                </div>
                            </>
                        ) : authMode === 'otp' ? (
                            <>
                                {!otpSent ? (
                                    <div style={styles.inputGroup}>
                                        <Mail size={18} style={styles.inputIcon} />
                                        <input
                                            type="tel"
                                            placeholder="Phone Number (+919876543210)"
                                            value={formData.phone}
                                            onChange={e => handleInputChange('phone', e.target.value)}
                                            style={styles.input}
                                            required
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div style={styles.infoBox}>
                                            <Check size={16} color={theme.colors.primary[500]} />
                                            <span style={{ fontSize: '13px' }}>OTP sent to {formData.phone}</span>
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <Lock size={18} style={styles.inputIcon} />
                                            <input
                                                type="text"
                                                placeholder="Enter 6-digit OTP"
                                                value={formData.otp}
                                                onChange={e => handleInputChange('otp', e.target.value)}
                                                style={styles.input}
                                                maxLength="6"
                                                required
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {step === 1 ? (
                                    <div style={styles.stepContainer}>
                                        <div style={styles.inputGroup}>
                                            <User size={18} style={styles.inputIcon} />
                                            <input
                                                type="text"
                                                placeholder="Full Name"
                                                value={formData.full_name}
                                                onChange={e => handleInputChange('full_name', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <User size={18} style={styles.inputIcon} />
                                            <input
                                                type="text"
                                                placeholder="Username"
                                                value={formData.username}
                                                onChange={e => handleInputChange('username', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <Mail size={18} style={styles.inputIcon} />
                                            <input
                                                type="email"
                                                placeholder="Email Address"
                                                value={formData.email}
                                                onChange={e => handleInputChange('email', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <Lock size={18} style={styles.inputIcon} />
                                            <input
                                                type="password"
                                                placeholder="Password"
                                                value={formData.password}
                                                onChange={e => handleInputChange('password', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <Lock size={18} style={styles.inputIcon} />
                                            <input
                                                type="password"
                                                placeholder="Confirm Password"
                                                value={formData.confirmPassword}
                                                onChange={e => handleInputChange('confirmPassword', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div style={styles.stepContainer}>
                                        <div style={styles.infoBox}>
                                            <Globe size={16} color={theme.colors.primary[500]} />
                                            <span style={{ fontSize: '13px' }}>We need your birth details to generate your personalized trading numerology.</span>
                                        </div>

                                        <div style={styles.inputGroup}>
                                            <User size={18} style={styles.inputIcon} />
                                            <input
                                                type="text"
                                                placeholder="Birth Name (as per records)"
                                                value={formData.birth_data.name}
                                                onChange={e => handleInputChange('birth_data.name', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>

                                        <div style={styles.inputGroup}>
                                            <MapPin size={18} style={styles.inputIcon} />
                                            <input
                                                type="text"
                                                placeholder="Place of Birth (Start typing...)"
                                                value={locationQuery}
                                                onChange={e => handleLocationSearch(e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                            {isSearchingLocation && (
                                                <Loader2 size={16} style={styles.loaderIcon} />
                                            )}

                                            {showSuggestions && locationSuggestions.length > 0 && (
                                                <div style={styles.suggestionsDropdown}>
                                                    {locationSuggestions.map((loc, index) => (
                                                        <div
                                                            key={index}
                                                            style={styles.suggestionItem}
                                                            onClick={() => selectLocation(loc)}
                                                        >
                                                            <MapPin size={14} style={{ marginRight: '8px', opacity: 0.7 }} />
                                                            {loc.display_name}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {(formData.birth_data.latitude && formData.birth_data.longitude) && (
                                            <div style={styles.coordsDisplay}>
                                                <span>Lat: {formData.birth_data.latitude.toFixed(4)}</span>
                                                <span>Long: {formData.birth_data.longitude.toFixed(4)}</span>
                                            </div>
                                        )}

                                        <div style={styles.row}>
                                            <div style={{ ...styles.inputGroup, flex: 1 }}>
                                                <Calendar size={18} style={styles.inputIcon} />
                                                <input
                                                    type="date"
                                                    value={`${formData.birth_data.year}-${String(formData.birth_data.month).padStart(2, '0')}-${String(formData.birth_data.day).padStart(2, '0')}`}
                                                    onChange={e => {
                                                        const date = new Date(e.target.value);
                                                        if (!isNaN(date.getTime())) {
                                                            handleInputChange('birth_data.day', date.getDate());
                                                            handleInputChange('birth_data.month', date.getMonth() + 1);
                                                            handleInputChange('birth_data.year', date.getFullYear());
                                                        }
                                                    }}
                                                    style={styles.input}
                                                    required
                                                />
                                            </div>
                                            <div style={{ ...styles.inputGroup, flex: 1 }}>
                                                <Clock size={18} style={styles.inputIcon} />
                                                <input
                                                    type="time"
                                                    value={`${String(formData.birth_data.hour).padStart(2, '0')}:${String(formData.birth_data.minute).padStart(2, '0')}`}
                                                    onChange={e => {
                                                        const [h, m] = e.target.value.split(':');
                                                        handleInputChange('birth_data.hour', parseInt(h));
                                                        handleInputChange('birth_data.minute', parseInt(m));
                                                    }}
                                                    style={styles.input}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {error && <div style={styles.error}>{error}</div>}
                        {success && <div style={styles.success}>{success}</div>}

                        <div style={styles.actions}>
                            {authMode === 'signup' && step === 2 && (
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    style={styles.backButton}
                                >
                                    <ArrowLeft size={16} /> Back
                                </button>
                            )}

                            <button
                                type="submit"
                                style={styles.submitButton}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    authMode === 'login' ? 'Sign In' :
                                        authMode === 'otp' ? (otpSent ? 'Verify & Login' : 'Send OTP') :
                                            (step === 1 ? 'Next Step' : 'Create Account')
                                )}
                                {!loading && authMode === 'signup' && step === 1 && <ArrowRight size={16} style={{ marginLeft: '8px' }} />}
                            </button>
                        </div>
                    </form>

                    {/* Full Screen Loader Overlay */}
                    {loading && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(2px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 100,
                            borderRadius: '24px',
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <Loader2 size={40} color={theme.colors.primary[500]} className="animate-spin" />
                                <span style={{ color: theme.colors.primary[600], fontWeight: '600' }}>Processing...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f8fafc', // Light background for the page
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '480px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        position: 'relative',
        animation: 'slideUp 0.3s ease-out',
    },
    header: {
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        padding: '32px 32px 24px',
        color: 'white',
        textAlign: 'center',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        marginBottom: '8px',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        fontSize: '15px',
        opacity: 0.9,
        lineHeight: '1.5',
    },
    content: {
        padding: '32px',
    },
    tabs: {
        display: 'flex',
        marginBottom: '32px',
        backgroundColor: '#f1f5f9',
        padding: '4px',
        borderRadius: '12px',
    },
    tab: {
        flex: 1,
        padding: '10px',
        border: 'none',
        background: 'none',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        borderRadius: '8px',
        transition: 'all 0.2s',
    },
    activeTab: {
        backgroundColor: 'white',
        color: '#0f172a',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    inputGroup: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    inputIcon: {
        position: 'absolute',
        left: '16px',
        color: '#94a3b8',
        pointerEvents: 'none',
    },
    input: {
        width: '100%',
        padding: '14px 16px 14px 48px',
        borderRadius: '12px',
        border: '1px solid #cbd5e1',
        fontSize: '15px',
        color: '#0f172a',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s',
        outline: 'none',
        fontWeight: '500',
    },
    row: {
        display: 'flex',
        gap: '12px',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
    },
    submitButton: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#4f46e5',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s',
    },
    backButton: {
        padding: '14px 20px',
        backgroundColor: 'transparent',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    error: {
        padding: '12px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        borderRadius: '8px',
        fontSize: '14px',
        textAlign: 'center',
    },
    success: {
        padding: '12px',
        backgroundColor: '#f0fdf4',
        color: '#16a34a',
        borderRadius: '8px',
        fontSize: '14px',
        textAlign: 'center',
    },
    suggestionsDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
        zIndex: 50,
        marginTop: '4px',
        maxHeight: '200px',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
    },
    suggestionItem: {
        padding: '12px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#334155',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.1s',
    },
    loaderIcon: {
        position: 'absolute',
        right: '16px',
        color: '#94a3b8',
        animation: 'spin 1s linear infinite',
    },
    infoBox: {
        padding: '12px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        color: '#1e40af',
        display: 'flex',
        gap: '8px',
        alignItems: 'start',
        marginBottom: '8px',
    },
    coordsDisplay: {
        fontSize: '12px',
        color: '#64748b',
        display: 'flex',
        gap: '12px',
        padding: '0 4px',
    },
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        animation: 'fadeIn 0.3s ease-out',
    }
};

// Add keyframes for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .suggestion-item:hover {
    background-color: #f8fafc;
  }
  input::placeholder {
    color: #64748b;
    opacity: 1;
  }
`;
document.head.appendChild(styleSheet);

export default AuthPage;
